import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faPlus, faTrash, faSearch, faTimes, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { TranslateModule } from '@ngx-translate/core';

interface Entity {
  id: number | string;
  label: string;
  location?: {
    box: [number, number, number, number];
    page: number;
  }[];
  probability: number;
  text: string;
}

interface ObjectValue {
  id: number | string;
  text: string;
  relation_score: number;
}

interface ObjectData {
  relation: string;
  values: {
    [key: string]: ObjectValue[];
  };
}

@Component({
  selector: 'app-entities-editor',
  standalone: true,
  imports: [CommonModule, FormsModule, FontAwesomeModule, TranslateModule],
  templateUrl: './entities-editor.component.html',
  styleUrls: ['./entities-editor.component.scss']
})
export class EntitiesEditorComponent implements OnChanges {
  @Input() entities: Entity[] = [];
  @Input() objects: ObjectData[] = [];
  @Output() contentChanged = new EventEmitter<void>();

  filterText: string = '';
  
  icons = {
    faPlus,
    faTrash,
    faSearch,
    faTimes,
    faChevronDown,
    faChevronUp
  };

  ngOnChanges(changes: SimpleChanges): void {
  }

  onContentChanged(): void {
    this.contentChanged.emit();
  }

  addEntity(): void {
    const newId = 'new_' + Date.now();
    this.entities.unshift({
      id: newId,
      label: 'NEW_ENTITY',
      text: '',
      probability: 1.0
    });
    this.onContentChanged();
  }

  deleteEntity(index: number): void {
    this.entities.splice(index, 1);
    this.onContentChanged();
  }

  addObject(): void {
    const obj: ObjectData = {
      relation: 'OBJECT_' + (this.objects.length + 1),
      values: {}
    };
    this.addObjectKey(obj);
    this.objects.unshift(obj);
    this.onContentChanged();
  }

  deleteObject(index: number): void {
    this.objects.splice(index, 1);
    this.onContentChanged();
  }

  addObjectKey(obj: ObjectData, keyName?: string): void {
    let nextNum = Object.keys(obj.values || {}).length + 1;
    let newKey = keyName || 'field_' + nextNum;
    
    while (obj.values && obj.values[newKey]) {
      nextNum++;
      newKey = 'field_' + nextNum;
    }

    if (!obj.values) obj.values = {};
    obj.values[newKey] = [];
    this.addObjectValue(obj, newKey);
    this.onContentChanged();
  }

  deleteObjectKey(obj: ObjectData, key: string): void {
    delete obj.values[key];
    this.onContentChanged();
  }

  updateObjectKey(obj: ObjectData, oldKey: string, newKey: string): void {
    if (oldKey === newKey || !newKey) return;
    if (obj.values[newKey]) return;
    
    obj.values[newKey] = obj.values[oldKey];
    delete obj.values[oldKey];
    this.onContentChanged();
  }

  addObjectValue(obj: ObjectData, key: string): void {
    const newId = 'new_val_' + Date.now();
    obj.values[key].push({
      id: newId,
      text: '',
      relation_score: 1.0
    });
    this.onContentChanged();
  }

  deleteObjectValue(obj: ObjectData, key: string, index: number): void {
    obj.values[key].splice(index, 1);
    this.onContentChanged();
  }

  getObjectKeys(values: any): string[] {
    return Object.keys(values || {});
  }

  getOccurrenceData(entity: Entity): { index: number, total: number } {
    const list = this.entities.filter(e => e.label === entity.label);
    return {
      index: list.findIndex(e => e.id === entity.id) + 1,
      total: list.length
    };
  }

  getObjOccurrenceData(obj: ObjectData, index: number): { index: number, total: number } {
    const list = this.objects.filter(o => o.relation === obj.relation);
    const pos = this.objects.slice(0, index + 1).filter(o => o.relation === obj.relation).length;
    return {
      index: pos,
      total: list.length
    };
  }

  formatLabel(label: string): string {
    if (!label) return '';
    return label
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }
}
